// Whole-activity peak metrics (matches training-analytics platforms).

import { computeEfficiencyFactor, computeIntensityFactor, computeTSS } from "./powerMetrics";

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function kmhFromMs(ms) {
  if (!isFiniteNumber(ms)) return null;
  return Math.round(ms * 3.6 * 10) / 10;
}

function canonicalMovingSpeedMs(activity) {
  if (isFiniteNumber(activity?.average_moving_speed_ms)) {
    return activity.average_moving_speed_ms;
  }
  if (isFiniteNumber(activity?.avg_speed_ms)) return activity.avg_speed_ms;
  return null;
}

function nums(list, key) {
  return list.map((s) => s[key]).filter((v) => v != null && !Number.isNaN(v));
}

function min(list) {
  return list.length ? Math.min(...list) : null;
}
function max(list) {
  return list.length ? Math.max(...list) : null;
}
function avg(list) {
  if (!list.length) return null;
  return list.reduce((a, b) => a + b, 0) / list.length;
}

function elevationDeltas(samples) {
  const alts = samples.map((s) => s.altitude_m).filter((a) => a != null);
  let gain = 0;
  let loss = 0;
  for (let i = 1; i < alts.length; i += 1) {
    const d = alts[i] - alts[i - 1];
    if (d > 0) gain += d;
    else if (d < 0) loss += -d;
  }
  return { gain, loss };
}

function decouplingPercent(samples) {
  const withBoth = samples.filter(
    (s) => s.power != null && s.hr != null && s.hr > 0
  );
  if (withBoth.length < 20) return null;

  const half = Math.floor(withBoth.length / 2);
  const first = withBoth.slice(0, half);
  const second = withBoth.slice(half);

  const efHalf = (slice) => {
    const p = avg(slice.map((s) => s.power));
    const h = avg(slice.map((s) => s.hr));
    if (!p || !h) return null;
    return p / h;
  };

  const a = efHalf(first);
  const b = efHalf(second);
  if (a == null || b == null || a === 0) return null;
  return ((a - b) / a) * 100;
}

/** Temperature summary from raw samples (for overview stat cards). */
export function getTemperatureStats(samples) {
  const temps = nums(samples, "temperature_c");
  if (!temps.length) return null;
  return {
    min: Math.round(min(temps) * 10) / 10,
    avg: Math.round(avg(temps) * 10) / 10,
    max: Math.round(max(temps) * 10) / 10,
  };
}

/**
 * Whole-activity Peak Metrics.
 *
 * NP, IF, TSS, moving speed, validated max speed, work, and elevation gain
 * come from activity-level canonical fields (same as Summary). Chart samples
 * stay for visualization and for metrics that have no stored whole-ride value
 * (temperature, Pw:HR, elevation loss). Range/lap helpers must not call this
 * for a slice.
 */
export function computePeakMetrics(activity, samples, opts = {}) {
  const durationSeconds =
    activity?.duration_seconds ?? activity?.moving_time_seconds ?? null;
  const distanceM = activity?.total_distance_m ?? null;
  const ftp = isFiniteNumber(activity?.effective_ftp_watts)
    ? activity.effective_ftp_watts
    : opts.ftp ?? null;
  const weightKg = opts.weightKg ?? null;

  const powers = nums(samples, "power");
  const hrs = nums(samples, "hr");
  const cadences = nums(samples, "cadence");
  const speedsMs = nums(samples, "speed_ms");
  const tempStats = getTemperatureStats(samples);

  const canonicalAvgPower = isFiniteNumber(activity?.avg_power)
    ? activity.avg_power
    : null;
  const canonicalAvgHr = isFiniteNumber(activity?.avg_hr) ? activity.avg_hr : null;
  const avgPower = canonicalAvgPower ?? avg(powers);
  const maxPower = isFiniteNumber(activity?.max_power)
    ? activity.max_power
    : max(powers);
  const minPower = min(powers);
  const avgHr = canonicalAvgHr ?? avg(hrs);
  const maxHr = isFiniteNumber(activity?.max_hr) ? activity.max_hr : max(hrs);
  const minHr = min(hrs);
  const avgCad = isFiniteNumber(activity?.avg_cadence)
    ? activity.avg_cadence
    : avg(cadences);
  const maxCad = isFiniteNumber(activity?.max_cadence)
    ? activity.max_cadence
    : max(cadences);
  const minCad = min(cadences);
  const movingSpeedMs = canonicalMovingSpeedMs(activity);
  const minSpeed = min(speedsMs);

  const np = isFiniteNumber(activity?.normalized_power)
    ? activity.normalized_power
    : null;
  const intensity =
    activity?.intensity_factor != null
      ? activity.intensity_factor
      : np && ftp
        ? computeIntensityFactor(np, ftp)
        : null;
  const tss =
    activity?.tss != null
      ? activity.tss
      : np && ftp && durationSeconds
        ? computeTSS(durationSeconds, np, ftp)
        : null;
  const vi = np && avgPower ? Math.round((np / avgPower) * 100) / 100 : null;
  const ef =
    np && avgHr ? computeEfficiencyFactor(np, Math.round(avgHr)) : null;
  const work =
    canonicalAvgPower && durationSeconds
      ? Math.round((canonicalAvgPower * durationSeconds) / 1000)
      : null;
  const wPerKg =
    avgPower && Number(weightKg) > 0
      ? Math.round((avgPower / Number(weightKg)) * 100) / 100
      : null;

  const hasAltData = (samples || []).some((s) => s.altitude_m != null);
  const elev = hasAltData ? elevationDeltas(samples) : { gain: 0, loss: 0 };
  const gain =
    activity?.elevation_gain_m != null
      ? Math.round(activity.elevation_gain_m)
      : hasAltData
        ? Math.round(elev.gain)
        : null;
  const loss = hasAltData ? Math.round(elev.loss) : null;

  const grade =
    distanceM > 0 && gain != null && loss != null
      ? Math.round(((gain - loss) / distanceM) * 100 * 10) / 10
      : null;

  const vam =
    gain != null && gain > 0 && durationSeconds > 0
      ? Math.round((gain / (durationSeconds / 3600)))
      : null;

  const pwHr = decouplingPercent(samples);

  return {
    duration_seconds: durationSeconds,
    distance_m: distanceM,
    tss,
    work_kj: work,
    np,
    intensity_factor: intensity,
    vi,
    pw_hr_pct: pwHr != null ? Math.round(pwHr * 100) / 100 : null,
    ef,
    elevation_gain_m: gain,
    elevation_loss_m: loss,
    grade_pct: grade,
    vam_mhr: vam,
    w_per_kg: wPerKg,

    min_power: minPower != null ? Math.round(minPower) : null,
    avg_power: avgPower != null ? Math.round(avgPower) : null,
    max_power: maxPower != null ? Math.round(maxPower) : null,

    min_hr: minHr != null ? Math.round(minHr) : null,
    avg_hr: avgHr != null ? Math.round(avgHr) : null,
    max_hr: maxHr != null ? Math.round(maxHr) : null,

    min_cadence: minCad != null ? Math.round(minCad) : null,
    avg_cadence: avgCad != null ? Math.round(avgCad) : null,
    max_cadence: maxCad != null ? Math.round(maxCad) : null,

    min_speed_kmh: minSpeed != null ? kmhFromMs(minSpeed) : null,
    avg_speed_kmh: kmhFromMs(movingSpeedMs),
    max_speed_kmh: kmhFromMs(
      isFiniteNumber(activity?.max_speed_ms) ? activity.max_speed_ms : null
    ),

    min_temperature_c: tempStats?.min ?? null,
    avg_temperature_c: tempStats?.avg ?? null,
    max_temperature_c: tempStats?.max ?? null,

    has_power: powers.length > 0,
    has_hr: hrs.length > 0,
    has_cadence: cadences.length > 0,
    has_speed:
      movingSpeedMs != null ||
      isFiniteNumber(activity?.max_speed_ms) ||
      speedsMs.length > 0,
    has_temperature: tempStats != null,
  };
}
